import { SetStateAction, useCallback, useEffect, useMemo, useRef } from "react"
import { NavigateOptions, useLocation, useNavigate } from "react-router-dom"

// a replacement for react-router's useSearchParams that returns a stable setSearchParams function
// https://github.com/remix-run/react-router/issues/9991#issuecomment-2282208096
function useSearchParams(defaultParams: Record<string, unknown> = {}) {
  const location = useLocation()
  const navigate = useNavigate()

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(location.search)

    Object.entries(defaultParams).forEach(([key, value]) => {
      if (!params.has(key)) {
        params.set(key, value?.toString() ?? "")
      }
    })

    return params
  }, [location.search, defaultParams])

  const searchParamsRef = useRef(searchParams)

  useEffect(() => {
    searchParamsRef.current = searchParams
  }, [searchParams])

  const setSearchParams = useCallback(
    (setter: SetStateAction<URLSearchParams>, options?: NavigateOptions) => {
      const newParams =
        typeof setter === "function" ? setter(searchParamsRef.current) : setter
      navigate(`?${newParams}`, options)
    },
    [navigate],
  )

  return [searchParams, setSearchParams] as const
}

export function useSearchParamsAsObject() {
  return Object.fromEntries(useSearchParams()[0].entries())
}

export { useSearchParams }
